/* eslint-disable no-lonely-if */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-unsafe-optional-chaining */
import { Box, Flex, Input, Text, Tooltip } from '@chakra-ui/react';
import { ReactGrid } from '@silevis/reactgrid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Table.scss';
import './DimensionsTableTheme.scss';

import FigmaListTreeIcon from 'components/Icons/FigmaListTreeIcon';
import FigmaInfoIcon from 'components/Icons/FigmaInfoIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FigmaFileDownloadIcon from 'components/Icons/FigmaFileDownloadIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import { AddRowCellTemplate } from 'components/AddRowCell';
import { DateCellTemplate } from 'components/DateCell';
import DeleteGroupModal from 'components/DeleteGroupModal';
import { DeleteRowCellTemplate } from 'components/DeleteRowCell';
import { DropdownCellTemplate } from 'components/DropdownCell';
import { FiltersCellTemplate } from 'components/FiltersCell';
import { HeaderRowTemplate } from 'components/HeaderRow';
import { TextCellTemplate } from 'components/TextCell';
import PlanPermission from 'container/PlanPermission';
import {
  AddDimensionHeader,
  DeleteDimensionHeader,
  DeleteDimensionProperty,
  FetchDimensionGroup,
  FetchDimensionItems,
  FetchDimensionProperties,
  ReorderDimensionProperty,
  UpdateDimensionPropertyBulk,
  UpdateDimensionPropertyName,
  UpdateDimensionValues,
  UpdatePropertyHeader,
  getAddItem,
  getBaseScenario,
  getDimensionProperty,
  getFilteredDimensionItems,
  getLoaderStatus,
  getPropertiesSectionStatus,
  getSelectedDimension,
  getSettingsSectionStatus,
  handleAddItemStatus,
  handleDimensionItemsChange,
  resetBuilderDimension,
  UpdateDimensionName,
  handleDimensionLoader,
  getDimensionLoaderStatus,
  DimensionTableReordering,
} from 'redux/BuilderModeSlice';
import { hasEndDate, hasStartDate } from 'utils/GlobalHelpers';
import { AddItemProperty, getDimensionFilters, handleDimFiltersChange } from 'redux/ItemPageSlice';

import { SelectCellTemplate } from './components/SelectCell';
import { getTableColumns, getTableRows, reorderArray } from './NewTableHelpers';
import SelectionActionBar from './SelectionActionBar';
import { getColumns, getRows } from './TableHelpers';
import { DimensionsTableSkeleton } from './DimensionsSkeletons';
import ActiveFiltersPopover from './ActiveFiltersPopover';
import AddPropertyModal from './AddPropertyModal';
import {
  accent,
  controlHeight,
  font,
  fontSize,
  neutral,
  radius,
  text as textToken,
} from './designTokens';

interface Focus {
  rowId: number | string;
  columnId: number | string;
}

const swapProperties = (columns: any, target: any, columnIds: any) => {
  const targetId = Number.parseInt(target, 10);
  // Convert columnIds to numbers for comparison
  const idsToSwap = columnIds.map((id: any) => Number.parseInt(id, 10));

  const targetIndex = columns.findIndex((column: any) => column.id === targetId);

  idsToSwap.forEach((id: any) => {
    const swapIndex = columns.findIndex(
      (column: any) => Number.parseInt(column.id, 10) === Number.parseInt(id, 10),
    );
    if (targetIndex !== -1 && swapIndex !== -1) {
      const temporary = columns[targetIndex];
      columns[targetIndex] = columns[swapIndex];
      columns[swapIndex] = temporary;
    }
  });
  return columns;
};

const NewDimensionBuilderTable = ({ planPermission }: any) => {
  const dispatch: any = useDispatch();
  const selectedDimension: any = useSelector(getSelectedDimension);
  const selectedScenario = useSelector(getBaseScenario);
  const filteredDimensionItem: any = useSelector(getFilteredDimensionItems);
  const dimProperties: any = useSelector(getDimensionProperty);
  const loaderStatus: any = useSelector(getLoaderStatus);
  const addItem: any = useSelector(getAddItem);
  const tableReference: any = useRef(null);
  const scrollContainerReference: any = useRef(null);
  const propertiesSectionStatus = useSelector(getPropertiesSectionStatus);
  const settingsSectionStatus = useSelector(getSettingsSectionStatus);
  // Either side panel eats the same header width, so both compact this row.
  const isSidePanelOpen = propertiesSectionStatus || settingsSectionStatus;
  const dimensionFilters = useSelector(getDimensionFilters);
  const dimensionLoader = useSelector(getDimensionLoaderStatus);
  const [dimensionProperties, setDimensionProperties] = useState<any>([]);
  const [isAddColumnOpen, setIsAddColumnOpen] = useState(false);

  // The grid's "+" names the column up front via a modal, then creates it and
  // refetches so the new column appears immediately.
  const handleAddColumn = (columnName: string) => {
    setIsAddColumnOpen(false);
    dispatch(
      AddDimensionHeader({
        dimensionId: selectedDimension?.id,
        data: { name: columnName },
        toaster: true,
      }),
    ).then(() => {
      dispatch(
        FetchDimensionProperties({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    });
  };

  useEffect(() => {
    if (dimProperties) {
      setDimensionProperties(dimProperties);
    }
  }, [dimProperties]);

  const [, setFocus] = useState<Focus>({ rowId: 'header', columnId: 'name' });
  const [tableData, setTableData]: any = useState();
  useEffect(() => {
    const getTableData = async () => {
      if (addItem) {
        dispatch(handleAddItemStatus(false));
      }
      if (selectedDimension && selectedScenario) {
        dispatch(handleDimensionLoader(true));
        await dispatch(
          FetchDimensionProperties({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        ).then((response: any) => {
          if (response?.payload?.properties) {
            response?.payload?.properties?.map((data: any) => {
              if (data?.data_format) {
                dispatch(
                  FetchDimensionGroup({
                    data,
                    parameters: { scenario_id: selectedScenario?.id },
                  }),
                );
              }
              return null;
            });
          }
        });
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
        dispatch(handleDimensionLoader(false));
      }
    };
    getTableData();
    setFocus({ rowId: 'header', columnId: 'name' });
  }, [selectedDimension, selectedScenario]);

  const rows: any = useMemo(() => {
    if (dimensionProperties?.length >= 0 && tableData?.length >= 0) {
      return selectedDimension?.name !== 'Time'
        ? getTableRows(tableData, dimensionProperties, selectedDimension)
        : getRows(tableData, dimensionProperties);
    }
    return null;
  }, [tableData, dimensionProperties]);

  const memorizedColumns = useMemo(() => {
    if (dimensionProperties?.length >= 0 && tableReference?.current?.offsetWidth) {
      return selectedDimension?.name !== 'Time'
        ? getTableColumns(
            dimensionProperties,
            tableReference?.current?.offsetWidth,
            selectedDimension,
          )
        : getColumns(dimensionProperties, tableReference?.current?.offsetWidth);
    }
    return null;
  }, [dimensionProperties, selectedDimension]);

  // It updates dimension properties to enable start and end date toggle
  function updateDimensionStartEnd(enable: boolean) {
    dispatch(
      UpdateDimensionName({
        item_id: selectedDimension?.id,
        request_body: {
          enable_start_end: enable ? 'true' : 'false',
        },
      }),
    );
  }

  // To check if dimensionProperties has start & end date, so enable start and end date toggle in
  useEffect(() => {
    if (dimensionProperties?.length > 0) {
      const startDate = dimensionProperties.some((item: any) => hasStartDate(item?.name));
      const endDate = dimensionProperties.some((item: any) => hasEndDate(item?.name));
      const shouldEnable = startDate || endDate;

      if (shouldEnable !== selectedDimension?.enable_start_end) {
        updateDimensionStartEnd(shouldEnable);
      }
    }
  }, [dimensionProperties]);

  const [columns, setColumns]: any = useState(memorizedColumns);
  const [filtersData, setFiltersData]: any = useState();
  const [gridProperties, setGridProperties] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    itemId: '',
    itemName: '',
    type: '',
    status: false,
  });

  useEffect(() => {
    if (tableReference?.current?.offsetWidth && dimensionProperties?.length >= 0) {
      if (selectedDimension?.name !== 'Time') {
        setColumns(
          getTableColumns(
            dimensionProperties,
            tableReference?.current?.offsetWidth,
            selectedDimension,
          ),
        );
      } else {
        setColumns(getColumns(dimensionProperties, tableReference?.current?.offsetWidth));
      }
    }
  }, [propertiesSectionStatus, settingsSectionStatus]);

  // Column widths are derived from the table's pixel width, which was measured
  // once and then only re-measured when a side panel toggled — so widening the
  // window left the old widths in place and dead space at the right edge.
  //
  // This listens on `window` rather than observing the table element, and that
  // is the whole point: the element's width is itself a *result* of recomputing
  // columns, so a ResizeObserver on it feeds its own output back in as input
  // and the widths oscillate. The window's size has no such dependency.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const onResize = () => {
      clearTimeout(timer);
      // Debounced: a drag-resize emits continuously and rebuilding every
      // column per event is wasted work.
      timer = setTimeout(() => {
        const width = tableReference?.current?.offsetWidth;
        if (!width || !(dimensionProperties?.length >= 0)) return;
        setColumns(
          selectedDimension?.name !== 'Time'
            ? getTableColumns(dimensionProperties, width, selectedDimension)
            : getColumns(dimensionProperties, width),
        );
      }, 150);
    };

    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [dimensionProperties, selectedDimension]);

  useEffect(() => {
    if (filtersData) {
      dispatch(
        handleDimensionItemsChange({
          datas: filtersData?.data,
          cell: filtersData?.cell,
          dimensionFilters,
        }),
      );
    }
  }, [filtersData]);

  useEffect(() => {
    if (filteredDimensionItem?.length >= 0) setTableData(filteredDimensionItem);
  }, [filteredDimensionItem]);

  useEffect(() => {
    if (memorizedColumns) setColumns(memorizedColumns);
  }, [memorizedColumns]);

  const handleAddRow = () => {
    let object: any = {};
    for (const number of dimensionProperties) {
      object[number?.id] = '';
    }
    object = { ...object, name: '', id: '', position: '' };
    setTableData([...tableData, object]);
    // rows = [header, ...data]; there's no footer row anymore, so the new
    // row's 0-indexed position is rows.length - 1 (was -2 when a footer row
    // was still counted in rows.length).
    setFocus({ rowId: rows?.length - 1, columnId: 'name' });
    setGridProperties({ focusLocation: { rowId: rows?.length - 1, columnId: 'name' } });
    // ReactGrid renders inside our own scrollable wrapper rather than owning
    // a scroll pane itself, so its focusLocation has no way to scroll that
    // ancestor — a row appended past the fold stayed clipped by the
    // container's overflow:hidden and was never reachable. Scroll to the
    // bottom ourselves once the new row has actually rendered.
    //
    // ReactGrid recalculates its own absolutely-positioned content height in
    // its own post-commit pass (it's a virtualization library, not a plain
    // DOM list), which lands after React's commit but isn't guaranteed to be
    // done by the time a single setTimeout(0) macrotask runs — so reading
    // `scrollHeight` right away could still see the height from *before* the
    // new row was added, landing the scroll one row short. Poll briefly
    // instead of guessing a fixed delay: keep nudging scrollTop to the
    // (possibly still-growing) bottom until it stops moving.
    let lastHeight = -1;
    let attempts = 0;
    const settleScroll = () => {
      const container = scrollContainerReference.current;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
      attempts += 1;
      if (container.scrollHeight !== lastHeight && attempts < 15) {
        lastHeight = container.scrollHeight;
        requestAnimationFrame(settleScroll);
      }
    };
    requestAnimationFrame(settleScroll);
  };

  useEffect(() => {
    if (addItem) {
      handleAddRow();
      // Reset on the next tick, not synchronously — addItem must only ever
      // trigger one row per Create-button click, but resetting it in the
      // same tick as handleAddRow's focusLocation update was racing
      // ReactGrid's own focus handling, so the first keystroke into the new
      // row's cell was being swallowed instead of starting the edit.
      setTimeout(() => dispatch(handleAddItemStatus(false)), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addItem]);

  useEffect(() => {
    return () => {
      setFocus({ rowId: 'header', columnId: 'name' });
      dispatch(resetBuilderDimension());
    };
  }, []);

  const updateHeading = async (data: any) => {
    await dispatch(
      UpdatePropertyHeader({
        propertyId: data?.columnId,
        data: {
          name: data?.newCell?.text,
        },
      }),
    ).then(() => {
      dispatch(
        FetchDimensionProperties({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    });
  };

  const updateCell = async (changes: any) => {
    const change = changes[0];
    const existingRow = rows?.filter((data: any) => {
      return change?.rowId === data?.rowId;
    });
    if (change?.newCell?.colType && change?.previousCell?.colType) {
      updateHeading(change);
    } else if (
      change?.newCell?.property &&
      !change?.previousCell?.text &&
      !existingRow[0]?.itemId
    ) {
      await dispatch(
        AddItemProperty({
          dimension_id: Number(selectedDimension?.id),
          request_body: {
            name: change?.newCell?.text,
            scenario_id: selectedScenario?.id,
          },
          toaster: true,
        }),
      );
      await dispatch(
        FetchDimensionItems({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    } else {
      if (change?.newCell?.property) {
        await dispatch(
          UpdateDimensionPropertyName({
            item_id: filteredDimensionItem[change?.rowId]?.id,
            name: change?.newCell?.text,
            toaster: true,
          }),
        );
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      } else {
        await dispatch(
          UpdateDimensionValues({
            item_id: filteredDimensionItem[change?.rowId]?.id,
            request_body: {
              property_id: change?.columnId,
              value: change?.newCell?.text,
              scenario_id: selectedScenario?.id,
            },
            toaster: true,
          }),
        );
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      }
    }
  };

  const updateCellBulk = async (changes: any) => {
    const newChanges: any[] = [];
    for (const change of changes) {
      const object = {
        item_id: `${filteredDimensionItem[change?.rowId]?.id}`,
        property_id: `${change?.columnId}`,
        value: change?.newCell?.text,
      };
      newChanges.push(object);
    }
    if (newChanges?.length > 0)
      await dispatch(
        UpdateDimensionPropertyBulk({
          request_body: {
            items: newChanges,
            dimension_id: selectedDimension?.id,
            scenario_id: selectedScenario?.id,
          },
          toaster: true,
        }),
      );
  };

  const updateCells = async (changes: any) => {
    if (addItem) dispatch(handleAddItemStatus(false));
    await (changes?.length > 1 ? updateCellBulk(changes) : updateCell(changes));
    const dimensionFilter = JSON.parse(JSON.stringify(dimensionFilters));
    if (dimensionFilters?.length > 0) {
      for (let index = 0; index < dimensionFilter?.length; index += 1) {
        if (dimensionFilter[index].id === changes[0].columnId) {
          const updatedValue = { name: changes[0]?.newCell?.text, status: true };
          dimensionFilter[index].data = [...dimensionFilter[index]?.data, updatedValue];
        }
      }
      dispatch(
        handleDimensionItemsChange({
          datas: filtersData?.data,
          cell: filtersData?.cell,
          dimensionFilters: dimensionFilter,
        }),
      );

      for (let index = 0; index < dimensionFilter?.length; index += 1) {
        const isCheckedData = dimensionFilter?.map((data: any) => data?.data);
        const isCheckedId = dimensionFilter?.map((data: any) => data?.id);
        dispatch(
          handleDimFiltersChange({
            ischeckedItemsChecked: isCheckedData[index],
            id: isCheckedId[index],
          }),
        );
      }
    }
  };

  const handleLocationChanged = (event: any) => {
    if (Object.keys(gridProperties)?.length > 0) {
      setGridProperties({});
    }
    setFocus(event);
    return true;
  };

  const handleColumnResize = (ci: any, width: number) => {
    setColumns((column: any) => {
      const columnIndex = column.findIndex((element: any) => element.columnId === ci);
      const resizedColumn = column[columnIndex];
      const updatedColumn = { ...resizedColumn, width };
      column[columnIndex] = updatedColumn;
      return [...column];
    });
  };

  const handleDeleteRow = async (data: any) => {
    if (addItem) {
      dispatch(handleAddItemStatus(false));
    }
    if (data?.id === '') {
      dispatch(
        FetchDimensionItems({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    } else {
      setDeleteModal({ itemId: data?.id, itemName: data?.itemName, type: 'row', status: true });
    }
  };

  const handleDeleteModal = async (type: string) => {
    if (type === 'header') {
      setDeleteModal({ itemId: '', itemName: '', type: '', status: false });
      dispatch(DeleteDimensionHeader({ item_id: deleteModal?.itemId })).then(() => {
        dispatch(
          FetchDimensionProperties({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      });
    } else {
      if (deleteModal?.itemId) {
        setDeleteModal({ itemId: '', itemName: '', type: '', status: false });
        await dispatch(
          DeleteDimensionProperty({
            item_id: deleteModal?.itemId,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
        dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      }
    }
  };

  const handleContextMenu = (
    selectedRowIds: any,
    selectedColIds: any,
    selectionMode: any,
    menuOptions: any,
    selectedRanges: any,
  ) => {
    const deleteRow = {
      id: 'deleteRow',
      label: 'Delete row',
      handler: () => {
        const filteredRows = rows?.filter((data2: any) => {
          return data2?.rowId === selectedRanges[0][0]?.rowId && data2;
        });
        handleDeleteRow({ id: filteredRows[0]?.itemId, itemName: filteredRows[0]?.itemName });
      },
    };

    const deleteColumn = {
      id: 'deleteRow',
      label: 'Delete Column',
      handler: () => {
        const filteredColumn = columns?.filter((data2: any) => {
          return data2?.columnId === selectedRanges[0][0]?.columnId && data2;
        });
        setDeleteModal({
          itemId: filteredColumn[0]?.columnId,
          itemName: filteredColumn[0]?.name,
          type: 'header',
          status: true,
        });
      },
    };
    if (
      selectedRanges[0] &&
      selectedRanges[0][0]?.rowId === 'header' &&
      selectedRanges[0][0]?.columnId !== 'name'
    ) {
      return [deleteColumn];
    }
    if (
      selectedRanges[0] &&
      selectedRanges[0][0]?.rowId !== 'header' &&
      selectedRanges[0][0]?.rowId !== 'footer'
    ) {
      let selectedRows = false;
      for (let index = 0; index < selectedRanges[0]?.length; index += 1) {
        selectedRows = selectedRanges[0][0]?.rowId === selectedRanges[0][index]?.rowId;
      }
      if (
        selectedRanges[0][0]?.columnId !== 'delete' &&
        selectedRows &&
        selectedDimension?.name !== 'Time'
      ) {
        menuOptions = menuOptions.filter((object: any) => object.id !== 'cut');
        return [...menuOptions, deleteRow];
      }
      return selectedRanges[0][0]?.columnId === 'delete' &&
        selectedRanges[0]?.length === 1 &&
        selectedDimension?.name !== 'Time'
        ? [deleteRow]
        : menuOptions.filter((object: any) => object.id !== 'cut');
    }
    return [];
  };

  const handleCanReorderRows = (targetRowId: any) => {
    return targetRowId !== 'footer' || targetRowId !== 'header';
  };
  const canReorderColumns = (targetColumnId: any) => {
    return targetColumnId !== 'name' || targetColumnId !== 'select';
  };

  const handleColumnsReorder = async (targetColumnId: any, columnIds: any) => {
    if (dimensionProperties?.length) {
      const properties = swapProperties(
        JSON.parse(JSON.stringify(dimensionProperties)),
        targetColumnId,
        columnIds,
      );

      setDimensionProperties([...properties]);

      const reorderedColumsn = properties
        .filter((col: any) => {
          return !Number.isNaN(Number.parseInt(col.id, 10));
        })
        .map((col: any) => {
          return Number.parseInt(col.id, 10);
        });

      dispatch(
        DimensionTableReordering({
          dimId: selectedDimension?.id,
          request_body: { dim_item_prop: reorderedColumsn },
        }),
      );
    }
  };

  const handleRowsReorder = async (targetRowId: any, rowIds: any[]) => {
    const to = tableData?.findIndex((person: any) => person.position - 1 === targetRowId);
    const rowsIds = rowIds.map((id) =>
      tableData?.findIndex((person: any) => person.position - 1 === id),
    );
    const reorededArray = reorderArray(tableData, rowsIds, to);
    await dispatch(
      ReorderDimensionProperty({
        selectedDimension: selectedDimension?.id,
        reorededArr: reorededArray,
        scenario_id: selectedScenario?.id,
      }),
    );
    dispatch(
      FetchDimensionItems({
        selectedId: selectedDimension?.id,
        parameters: { scenario_id: selectedScenario?.id },
      }),
    );
  };

  const handleDeleteHeader = (data: any) =>
    setDeleteModal({ itemId: data?.id, itemName: data?.text, type: 'header', status: true });

  const handleFilterChange = (cell: any, data: any) => setFiltersData({ cell, data });

  const [tableSearchTerm, setTableSearchTerm] = useState('');

  // Search bar was UI-only (state tracked, never applied) — filter the grid's
  // display rows by item name here, but keep `rows` itself untouched since
  // rowId is a raw index into tableData that add/edit/focus logic elsewhere
  // relies on. Only the "name" column is a fair target — Time dimension rows
  // have no name filter option in Figma, so this covers every other table.
  const displayedRows = useMemo(() => {
    if (!tableSearchTerm.trim()) return rows;
    const term = tableSearchTerm.trim().toLowerCase();
    return rows?.filter(
      (row: any) => row?.rowId === 'header' || row?.itemName?.toLowerCase()?.includes(term),
    );
  }, [rows, tableSearchTerm]);

  return (
    <Flex
      height='100%'
      width='100%'
      flexDirection='column'
      ref={tableReference}
      flex={1}
      overflow='hidden'
      position='relative'
      bg={neutral.surface}
      p={4}
      borderRadius={radius.surface}
      border={`1px solid ${neutral.border}`}
    >
      <Flex
        justifyContent='space-between'
        alignItems='center'
        flexWrap='wrap'
        width='calc(100% + 32px)'
        mx='-16px'
        mt='-16px'
        mb='12px'
        px='16px'
        py='8px'
        gap='12px'
        borderBottom={`1px solid ${neutral.borderSubtle}`}
      >
        <Flex alignItems='center' gap='12px' minW={0}>
          <FigmaListTreeIcon width={18} height={18} color={textToken.secondary} />
          <Tooltip isDisabled label={selectedDimension?.name}>
            <Text
              fontSize={fontSize.subtitle}
              fontWeight='600'
              fontFamily={font.body}
              color={textToken.primary}
              whiteSpace='nowrap'
              overflow='hidden'
              textOverflow='ellipsis'
              maxW='240px'
            >
              {selectedDimension?.name}
            </Text>
          </Tooltip>
          {filteredDimensionItem?.length ? (
            <Flex
              alignItems='center'
              justifyContent='center'
              bg={neutral.surfaceMuted}
              border={`1px solid ${neutral.border}`}
              borderRadius={radius.control}
              px='8px'
              py='2px'
              flexShrink={0}
            >
              <Text
                fontSize={fontSize.label}
                fontWeight='500'
                fontFamily={font.body}
                color={textToken.secondary}
                whiteSpace='nowrap'
              >
                {filteredDimensionItem.length} items
              </Text>
            </Flex>
          ) : null}
          {selectedDimension?.description ? (
            <Tooltip label={selectedDimension.description}>
              <Box display='flex'>
                <FigmaInfoIcon width={14} height={14} color={textToken.muted} />
              </Box>
            </Tooltip>
          ) : null}
        </Flex>
        <Flex alignItems='center' gap='12px' flexWrap='wrap'>
          <Flex
            alignItems='center'
            gap='8px'
            bg={neutral.surface}
            border={`1px solid ${neutral.border}`}
            borderRadius={radius.control}
            px='12px'
            height={controlHeight}
            w={isSidePanelOpen ? '150px' : '219px'}
            flexShrink={1}
            minW='110px'
            transition='border-color 0.12s ease'
            _focusWithin={{ borderColor: accent.border }}
          >
            <FigmaSearchIcon width={13} height={13} color={textToken.muted} />
            <Input
              variant='unstyled'
              placeholder='Search items...'
              value={tableSearchTerm}
              onChange={(event) => setTableSearchTerm(event.target.value)}
              fontSize={fontSize.body}
              fontFamily={font.body}
              color={textToken.primary}
              _placeholder={{ color: textToken.muted }}
              sx={{ '&:focus::placeholder': { opacity: 0 } }}
            />
          </Flex>
          <ActiveFiltersPopover
            dimensionProperties={dimensionProperties}
            setFiltersData={setFiltersData}
            compact={isSidePanelOpen}
          />
          <Flex
            as='button'
            alignItems='center'
            gap='8px'
            bg={neutral.surface}
            border={`1px solid ${neutral.border}`}
            borderRadius={radius.control}
            px={isSidePanelOpen ? '9px' : '12px'}
            height={controlHeight}
            cursor='pointer'
            flexShrink={0}
            transition='background 0.12s ease, border-color 0.12s ease'
            _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
          >
            <FigmaFileDownloadIcon width={14} height={14} color={textToken.secondary} />
            {!isSidePanelOpen && (
              <Text
                fontSize={fontSize.body}
                fontWeight='500'
                fontFamily={font.body}
                color={textToken.primary}
                whiteSpace='nowrap'
              >
                Download
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
      {!planPermission && <SelectionActionBar />}
      {dimensionLoader || loaderStatus ? (
        <DimensionsTableSkeleton />
      ) : (
        <>
          {columns?.length && rows?.length ? (
            <>
              {/* flex='1' minH={0} is what actually lets this area scroll: the
                  root Flex is height-bounded + overflow:hidden with no scroll
                  of its own, so without an explicit shrinkable flex basis here,
                  this box just grew to fit every row (including newly-added
                  ones past the fold) and the root silently clipped the excess
                  instead of the table ever scrolling to reveal it. */}
              <Box
                position='relative'
                border={`1px solid ${neutral.border}`}
                // Must match the pane/corner-cell radii in
                // DimensionsTableTheme.scss. When the shell clipped at 8px
                // while ReactGrid's corner cells rounded at 7px (and 10px from
                // the shared Table.scss), the mismatch showed as a sliver of
                // double outline at each corner.
                borderRadius={radius.surface}
                overflow='hidden'
                flex='1'
                minH={0}
                display='flex'
                flexDirection='column'
              >
                <Box
                  ref={scrollContainerReference}
                  className='md-table-container sm-scroll dimensions-items-grid'
                  width={'100%'}
                  flex='1'
                  minH={0}
                  overflowY='auto'
                >
                  <ReactGrid
                    rows={displayedRows}
                    columns={columns}
                    enableFillHandle
                    enableRangeSelection
                    enableColumnSelection={true}
                    stickyLeftColumns={columns?.length <= 2 ? 0 : 1}
                    stickyBottomRows={0}
                    stickyTopRows={1}
                    enableRowSelection={rows?.length > 3}
                    verticalStickyBreakpoint={100}
                    horizontalStickyBreakpoint={100}
                    onRowsReordered={handleRowsReorder}
                    {...gridProperties}
                    onFocusLocationChanging={handleLocationChanged}
                    onCellsChanged={planPermission ? undefined : updateCells}
                    onContextMenu={planPermission ? undefined : handleContextMenu}
                    onColumnResized={planPermission ? undefined : handleColumnResize}
                    canReorderRows={handleCanReorderRows}
                    canReorderColumns={canReorderColumns}
                    onColumnsReordered={handleColumnsReorder}
                    customCellTemplates={{
                      header: new HeaderRowTemplate(),
                      delete: new DeleteRowCellTemplate(),
                      text: new TextCellTemplate(handleDeleteHeader),
                      date: new DateCellTemplate(),
                      addRow: new AddRowCellTemplate(handleAddRow, planPermission),
                      dropdown: new DropdownCellTemplate(handleDeleteRow, planPermission),
                      filter: new FiltersCellTemplate(handleFilterChange),
                      select: new SelectCellTemplate(),
                    }}
                  />
                </Box>
                {!planPermission && (
                  <Flex
                    as='button'
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsAddColumnOpen(true);
                    }}
                    position='absolute'
                    top='0'
                    right='0'
                    zIndex={2}
                    // Matches the header row height so the button sits flush
                    // inside it rather than bleeding into the first data row.
                    width='34px'
                    height='34px'
                    bg={accent.solid}
                    borderTopRightRadius={radius.surface}
                    alignItems='center'
                    justifyContent='center'
                    transition='background 0.12s ease'
                    _hover={{ bg: accent.hover }}
                  >
                    <FigmaPlusIcon width={13} height={13} color={textToken.inverse} />
                  </Flex>
                )}
                <AddPropertyModal
                  isOpen={isAddColumnOpen}
                  onClose={() => setIsAddColumnOpen(false)}
                  onCreate={handleAddColumn}
                />
              </Box>
              {!planPermission && (
                <Flex
                  as='button'
                  onClick={handleAddRow}
                  alignItems='center'
                  justifyContent='center'
                  gap='8px'
                  width='100%'
                  mt='12px'
                  py='8px'
                  px='12px'
                  bg='white'
                  border='1px solid #D1D6DD'
                  borderRadius={radius.control}
                  cursor='pointer'
                  flexShrink={0}
                  transition='background 0.15s ease, transform 0.1s ease'
                  _hover={{ bg: '#F5F0FF' }}
                  _active={{ bg: '#F5F0FF', transform: 'scale(0.97)' }}
                >
                  <FigmaPlusIcon width={13} height={13} color='#121826' />
                  <Text
                    fontSize='13px'
                    fontWeight='500'
                    fontFamily='Roboto Medium, Roboto Regular'
                    color='#121826'
                  >
                    Add New item
                  </Text>
                </Flex>
              )}
            </>
          ) : (
            'NA'
          )}
        </>
      )}
      {deleteModal.status && (
        <DeleteGroupModal
          isOpen={deleteModal.status}
          onClose={() => setDeleteModal({ itemId: '', itemName: '', type: '', status: false })}
          itemName={deleteModal.itemName || 'some name'}
          onDelete={() => handleDeleteModal(deleteModal.type)}
          orgChart={true}
        />
      )}
    </Flex>
  );
};

export default PlanPermission(NewDimensionBuilderTable);
